import { useEffect, useRef, useState, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import { Loader2, Trash2, Upload, LogOut, ImagePlus, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { COMPANY } from "@/data/site";
import ChangePasswordCard from "@/components/ChangePasswordCard";
import ChangeEmailCard from "@/components/ChangeEmailCard";

const CATS = [
  { id: "render-insulation",   label: "Render & Insulation" },
  { id: "domestic-extensions", label: "Domestic & Extensions" },
  { id: "commercial",          label: "Commercial" },
  { id: "landscaping",         label: "Landscaping" },
];

const AdminDashboard = () => {
  const { user, ready, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("render-insulation");
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [g, c] = await Promise.all([
        api.get("/gallery"),
        api.get("/contacts").catch(() => ({ data: [] })),
      ]);
      setItems(g.data || []);
      setContacts(c.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (ready && user) load(); }, [ready, user, load]);

  if (ready && !user) return <Navigate to="/admin/login" replace />;
  if (!ready) {
    return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-crest-green" /></div>;
  }

  const onUpload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Please choose an image."); return; }
    if (!file.type.startsWith("image/")) { toast.error("Only image files allowed."); return; }

    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", active);
    fd.append("title", title);

    setUploading(true);
    try {
      const { data } = await api.post("/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setItems((prev) => [data, ...prev]);
      setTitle("");
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Uploaded.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this image from the public gallery?")) return;
    try {
      await api.delete(`/gallery/${id}`);
      setItems((prev) => prev.filter((p) => p.id !== id));
      toast.success("Deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const inActive = items.filter((i) => i.category === active);

  return (
    <div className="min-h-[80vh] bg-crest-surface" data-testid="admin-dashboard-page">
      <div className="bg-crest-navy text-white">
        <div className="mx-auto max-w-7xl px-6 py-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-crest-gold">Cresttor admin</p>
            <h1 className="mt-2 font-display text-2xl md:text-3xl font-bold">Gallery management</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-white/70 hover:text-white" data-testid="admin-view-site">View public site →</Link>
            <button onClick={logout} data-testid="admin-logout"
              className="inline-flex items-center gap-2 rounded-md border border-white/20 px-4 py-2 text-sm hover:bg-white hover:text-crest-navy transition-colors duration-200">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 py-10">
        <Tabs value={active} onValueChange={setActive} className="w-full">
          <TabsList className="bg-white border border-border h-auto flex-wrap gap-1 p-1.5" data-testid="admin-tabs">
            {CATS.map((c) => (
              <TabsTrigger key={c.id} value={c.id} className="data-[state=active]:bg-crest-green data-[state=active]:text-white px-4 py-2" data-testid={`admin-tab-${c.id}`}>
                {c.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {CATS.map((c) => (
            <TabsContent key={c.id} value={c.id} className="mt-6">
              <form onSubmit={onUpload} className="rounded-lg bg-white border border-border p-6" data-testid={`upload-form-${c.id}`}>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 grid place-items-center rounded-md bg-crest-green-100 text-crest-green">
                    <ImagePlus className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="font-display text-lg font-semibold text-crest-navy">Upload to {c.label}</h2>
                    <p className="text-xs text-muted-foreground">JPEG, PNG or WebP · max 12MB</p>
                  </div>
                </div>
                <div className="mt-5 grid md:grid-cols-3 gap-4 items-end">
                  <div className="md:col-span-1">
                    <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 font-medium">Title (optional)</label>
                    <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Mossley Hill render" className="mt-2 h-11" data-testid={`upload-title-${c.id}`} />
                  </div>
                  <div className="md:col-span-1">
                    <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 font-medium">Image file</label>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      className="mt-2 block w-full h-11 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-crest-green file:px-4 file:py-2 file:text-white file:font-medium file:cursor-pointer file:hover:bg-crest-green-700"
                      data-testid={`upload-file-${c.id}`}
                    />
                  </div>
                  <button type="submit" disabled={uploading || active !== c.id}
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-crest-green px-5 py-3 text-sm font-semibold text-white hover:bg-crest-green-700 transition-colors duration-200 disabled:opacity-60 h-11"
                    data-testid={`upload-submit-${c.id}`}>
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    Upload
                  </button>
                </div>
              </form>

              <div className="mt-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display text-lg font-semibold text-crest-navy">Photos in {c.label}</h3>
                  <span className="text-xs text-muted-foreground">{inActive.length} item{inActive.length !== 1 ? "s" : ""}</span>
                </div>
                {(() => {
                  if (loading) {
                    return <div className="grid place-items-center py-16"><Loader2 className="h-7 w-7 animate-spin text-crest-green" /></div>;
                  }
                  if (inActive.length === 0) {
                    return (
                      <div className="rounded-lg border border-dashed border-border bg-white p-10 text-center text-muted-foreground" data-testid={`empty-${c.id}`}>
                        No images uploaded yet. Use the form above to add some.
                      </div>
                    );
                  }
                  return (
                    <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {inActive.map((it) => (
                        <div key={it.id} className="group relative rounded-lg border border-border bg-white overflow-hidden" data-testid={`admin-item-${it.id}`}>
                          <div className="aspect-square overflow-hidden">
                            <img src={fileUrl(it.image_url)} alt={it.title || ""} className="w-full h-full object-cover" loading="lazy" />
                          </div>
                          {it.title && (
                            <div className="px-3 py-2 text-xs font-medium text-crest-navy truncate">{it.title}</div>
                          )}
                          <button
                            onClick={() => onDelete(it.id)}
                            className="absolute top-2 right-2 h-9 w-9 grid place-items-center rounded-md bg-white/95 border border-border text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                          aria-label="Delete image"
                          data-testid={`delete-${it.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* Submissions */}
        <section className="mt-14 rounded-lg bg-white border border-border p-6" data-testid="admin-submissions">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 grid place-items-center rounded-md bg-crest-green-100 text-crest-green">
              <Mail className="h-5 w-5" />
            </div>
            <div>
              <h2 className="font-display text-lg font-semibold text-crest-navy">Recent submissions</h2>
              <p className="text-xs text-muted-foreground">Contact &amp; quote requests sent to {COMPANY.email}</p>
            </div>
          </div>
          {contacts.length === 0 ? (
            <div className="mt-5 rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              No submissions yet.
            </div>
          ) : (
            <div className="mt-5 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-[0.14em] text-muted-foreground border-b border-border">
                  <tr>
                    <th className="py-2 pr-4">When</th>
                    <th className="py-2 pr-4">Type</th>
                    <th className="py-2 pr-4">Name</th>
                    <th className="py-2 pr-4">Contact</th>
                    <th className="py-2 pr-4">Service / Subject</th>
                    <th className="py-2 pr-4">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.slice(0, 30).map((c) => (
                    <tr key={c.id} className="border-b border-border last:border-0 align-top">
                      <td className="py-3 pr-4 whitespace-nowrap text-xs text-muted-foreground">{new Date(c.created_at).toLocaleString("en-GB")}</td>
                      <td className="py-3 pr-4">
                        <span className={`text-xs uppercase tracking-[0.14em] font-medium ${c.type === "quote" ? "text-crest-gold" : "text-crest-green"}`}>
                          {c.type}
                        </span>
                      </td>
                      <td className="py-3 pr-4 font-medium text-crest-navy">{c.name}</td>
                      <td className="py-3 pr-4 text-xs">
                        <div>{c.email}</div>
                        {c.phone && <div className="text-muted-foreground">{c.phone}</div>}
                      </td>
                      <td className="py-3 pr-4">{c.service || c.subject || "—"}</td>
                      <td className="py-3 pr-4 max-w-md text-muted-foreground truncate" title={c.message}>{c.message || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <ChangePasswordCard />
        <ChangeEmailCard />
      </div>
    </div>
  );
};

export default AdminDashboard;
